import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Paper, Button, TextField, MenuItem,
  Alert, CircularProgress, Card, CardContent, Chip, Grid
} from '@mui/material';
import {
  HelpOutline as QueryIcon,
  Add as AddIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { parentAPI } from '../../services/api';
import MobileFormDialog from '@components/mobile/MobileFormDialog';

const STATUS_COLORS = { open: 'warning', in_progress: 'info', completed: 'success', void: 'default' };
const STATUS_LABELS = { open: 'Open', in_progress: 'In Progress', completed: 'Completed', void: 'Closed' };

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export default function MyQueries() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [queries, setQueries]   = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [newDialog, setNewDialog] = useState(false);
  const [form, setForm] = useState({ student_id: '', subject: '', body: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [qRes, cRes] = await Promise.all([
        parentAPI.queries.list(),
        parentAPI.getChildren(),
      ]);
      setQueries(qRes.data.queries || []);
      setChildren(cRes.data.children || []);
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.student_id || !form.subject.trim() || !form.body.trim()) {
      setError('Please fill in all required fields');
      return;
    }
    setSubmitting(true); setError('');
    try {
      await parentAPI.queries.create(form);
      setSuccess('Query submitted. The school team will be in touch.');
      setNewDialog(false);
      setForm({ student_id: '', subject: '', body: '' });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit query');
    } finally {
      setSubmitting(false);
    }
  };

  const openCount = queries.filter(q => q.status === 'open' || q.status === 'in_progress').length;

  return (
    <>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={2}
          mb={3}
        >
          <Box display="flex" alignItems="center" gap={2} flex={1}>
            <QueryIcon color="primary" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight={700}>My Queries</Typography>
              <Typography variant="body2" color="text.secondary">
                {openCount > 0 ? `${openCount} open` : 'All queries resolved'}
              </Typography>
            </Box>
          </Box>
          <Button
            sx={{ ml: { sm: 'auto' } }}
            variant="contained" startIcon={<AddIcon />}
            onClick={() => setNewDialog(true)}
          >
            New Query
          </Button>
        </Box>

        {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
        ) : queries.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <QueryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary" mb={2}>You haven't raised any queries yet.</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewDialog(true)}>
              Raise a Query
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {queries.map(q => (
              <Grid item xs={12} key={q.id}>
                <Card elevation={2} sx={{ borderLeft: '4px solid', borderColor: STATUS_COLORS[q.status] + '.main' }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="subtitle1" fontWeight={700}>{q.subject}</Typography>
                      <Chip label={STATUS_LABELS[q.status]} color={STATUS_COLORS[q.status]} size="small" />
                    </Box>
                    <Typography variant="body2" color="text.secondary" mb={1}>{q.body}</Typography>
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <Typography variant="caption" color="text.secondary">Student: <strong>{q.student_name}</strong></Typography>
                      <Typography variant="caption" color="text.secondary">Raised: {fmtDate(q.created_at)}</Typography>
                      {q.assigned_to_name && (
                        <Typography variant="caption" color="text.secondary">Assigned: <strong>{q.assigned_to_name}</strong></Typography>
                      )}
                    </Box>
                    {q.status === 'completed' && q.resolution_note && (
                      <Box mt={1.5} p={1.5} bgcolor="success.50" borderRadius={1} border="1px solid" borderColor="success.200">
                        <Typography variant="caption" color="success.dark">
                          <strong>Resolution:</strong> {q.resolution_note}
                        </Typography>
                        {q.completed_at && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Resolved on {fmtDate(q.completed_at)}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* New query dialog */}
      <MobileFormDialog
        open={newDialog}
        onClose={() => setNewDialog(false)}
        title="Raise a New Query"
        actions={
          <>
            <Button onClick={() => setNewDialog(false)}>Cancel</Button>
            <Button
              variant="contained" startIcon={<SendIcon />}
              onClick={handleSubmit} disabled={submitting}
            >
              {submitting ? 'Submitting…' : 'Submit Query'}
            </Button>
          </>
        }
      >
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              select fullWidth label="About which child? *" value={form.student_id}
              onChange={e => setForm(f => ({ ...f, student_id: e.target.value }))}
            >
              {children.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.name} ({c.admission_number})</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Subject *" placeholder="Brief description of your query"
              value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth multiline minRows={4} label="Details *"
              placeholder="Please describe your query in detail…"
              value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
            />
          </Grid>
        </Grid>
      </MobileFormDialog>
    </>
  );
}
